import {
  useCallback, useEffect, useRef, useState
} from 'react'
import {
  API_BASE_URL
} from '../config'
import {
  getUserFacingApiErrorMessage,
  integrationSuggestionsService,
} from '../services/api'
import type {
  ConnectableSuggestion,
  IntegrationSuggestionItem,
} from '../services/api'
import {
  OnboardingEvents,
  trackEvent
} from '../analytics/posthog'
import type {
  IntegrationSuggestionSurface
} from '../analytics/posthog'
import {
  isMobileBrowser
} from '../utils/inAppBrowser'

const EXPECTED_CALLBACK_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin
  } catch {
    return null
  }
})()

// The exact message type the callback page will post for this suggestion, or
// null when its flow posts nothing at all.
//
// Deliberately exact rather than a `*-oauth` wildcard. Several of these
// listeners are mounted on the integrations page at once, and every one of them
// is a bare `window.addEventListener('message', …)` — so a wildcard makes each
// connect resolve *every* listener. Matching an unrelated in-flight
// `${integration}-oauth` from a pairing modal would settle whichever suggestion
// this hook happened to have active, marking a tool connected that the user
// never touched.
//
// `promoted` returns null: its custom-integration callback page was written for
// a chat link, so it posts nothing and does not self-close. That flow resolves
// through the popup-closed poll below instead — see the caveat on `connect`.
function expectedMessageType(suggestion: IntegrationSuggestionItem): string | null {
  switch (suggestion.connect_method) {
    case 'pipedream_app':
      return 'pipedream-app-oauth'
    case 'integration_oauth':
    case 'integration_pipedream':
      return `${suggestion.integration_name}-oauth`
    default:
      return null
  }
}

// Not exported: no caller branches on it, so an exported union would be surface
// nobody consumes (knip enforces this). The three values still earn their place
// internally — `closed` genuinely differs from `connected`, because a user who
// shuts the popup leaves us unable to tell success from abandonment, which is
// exactly why the caller is asked to re-read the server.
//
// It is still handed to `onSettled`, though every caller currently ignores it.
// That is deliberate rather than leftover: it is how the hook says *how* the
// attempt ended, a caller with a cheap enough check could reasonably skip the
// re-read on a `connected` callback, and the hook's own tests read it to pin
// that a callback settle and a popup close stay distinguishable.
type ConnectOutcome = 'connected' | 'failed' | 'closed'

// What the server says about the attempt, once the caller has re-read the
// shortlist. Exported because both callers have to name it.
//
// This is the authority, not the postMessage: a connected app drops out of the
// shortlist, which is the one check that works identically for all four connect
// backends — including the promoted flow, whose callback page posts nothing at
// all. `unknown` is the honest answer when that re-read itself failed, and is
// kept distinct from `not_connected` so an outage of ours never lands in a
// count of what users chose.
export type SuggestionConnectVerdict = 'connected' | 'not_connected' | 'unknown'

// The outcomes a caller is asked about. A failed connect is not among them: it
// reached no provider, or the provider said no, so there is nothing to re-read
// and the hook has already counted it.
type SettledOutcome = Exclude<ConnectOutcome, 'failed'>

/**
 * Reads a verdict out of a freshly-fetched shortlist — `null` meaning the fetch
 * itself failed.
 *
 * Lives here, beside the events it feeds, because both surfaces answer the
 * question the same way and a third would too: the server stops suggesting what
 * is already connected, so "gone from a fresh read" is the one check that holds
 * for all four connect backends. Written out per caller it was seven lines of
 * judgement duplicated twice, and the half that matters is the easiest to get
 * wrong — a failed read reported as `not_connected` files our own outage as a
 * user deciding against the tool.
 *
 * A caller with a better source is still free to skip this and return its own
 * verdict; that is why `onSettled` speaks in verdicts rather than shortlists.
 * The Pipedream browse card is the concrete case — it holds a real per-app
 * connection list, which is stronger evidence than this heuristic.
 */
export function verdictFromShortlist(
  stillSuggested: Set<string> | null,
  catalogKey: string,
): SuggestionConnectVerdict {
  if (stillSuggested === null) return 'unknown'
  return stillSuggested.has(catalogKey) ? 'not_connected' : 'connected'
}

/**
 * Presents a Pipedream catalog app to this hook, which speaks in suggestions.
 *
 * `connect_method: 'pipedream_app'` is the load-bearing field: it routes the
 * connect to `pipedreamAppsService.getAuthUrl(slug, mode)` and makes the
 * listener above expect `pipedream-app-oauth`, the message that flow posts. Any
 * other value silently connects nothing, which is why it is pinned here once
 * rather than restated by each surface that offers a Pipedream app.
 *
 * `logo_domain` stays null: it is the suggestion tiles' fallback for apps with
 * no logo of their own, and these callers either carry Pipedream's `img_src` or
 * draw their own brand icon.
 *
 * `accountLabel` adds another account of an app the user already has, under
 * that name (GET-17441). Left out, the connect is the app's first account.
 */
export function pipedreamAppSuggestion(
  slug: string,
  displayName: string,
  logoUrl: string | null = null,
  accountLabel?: string,
): ConnectableSuggestion {
  return {
    catalog_key: slug,
    display_name: displayName,
    integration_name: slug,
    connect_method: 'pipedream_app',
    logo_url: logoUrl,
    logo_domain: null,
    ...(accountLabel ? { account_label: accountLabel } : {}),
  }
}

// Why a connect ended without one. These four take different fixes, which is
// the whole point of splitting them: `popup_blocked` is a browser setting the
// user can change, `url_error` is ours (the connect endpoint or the network),
// `callback_failed` is the provider's own OAuth page reporting a denial, and
// `navigation_no_op` is a redirect the browser accepted and then never
// performed — the one that leaves no trace anywhere else.
type ConnectFailureReason =
  | 'popup_blocked'
  | 'url_error'
  | 'callback_failed'
  | 'navigation_no_op'

interface ConnectFailure {
  // Shown to the user. Bundled with `reason` rather than passed beside it so a
  // failure cannot be settled without also being counted.
  message: string
  reason: ConnectFailureReason
}

// `finish`'s arguments, as a union of tuples rather than a trailing optional:
// an optional would still admit `finish('failed')`, which sets no banner and
// emits no event — precisely the hole the bundling above exists to close. This
// shape makes the compiler reject it, and destructuring it in the parameter
// list gets TS to narrow `failure` from the outcome (dependent-parameter
// narrowing), so the body needs no redundant undefined check.
type FinishArgs =
  | ['connected']
  | ['closed']
  | ['failed', ConnectFailure]

// How long after `window.location.assign` a still-alive page is taken as proof
// the redirect was swallowed.
//
// A real navigation normally tears this document down long before the timer
// fires, and `pagehide`/`visibilitychange` cancel it when that teardown is slow
// — so the only thing left to trip it is an assign the browser accepted and
// silently ignored (observed on iOS Safari, most likely inside an in-app
// WKWebView; see the comment block in `utils/inAppBrowser.ts`). Three seconds
// rather than one: the target is a cross-origin authorize URL, so a slow mobile
// connection can legitimately leave the page alive for a while, and a spurious
// banner on a page that is about to vanish is worse than a slightly longer
// spinner.
const REDIRECT_WATCHDOG_MS = 3000

// How long an attempt may run without settling itself before it is given up on.
//
// The popup branch had no ceiling at all. It settles on the callback's
// `postMessage` or on the close-poll seeing `popup.closed`, so a popup the user
// neither completes nor closes fires neither and the attempt stays open
// forever — a tile spinning with no banner and no retry, behind an in-flight
// guard that discarded every later tap on the grid. That is the fourth branch
// GET-11630 removed from the redirect path, reached by a different road
// (GET-13089).
//
// Armed the moment an attempt is claimed, before either transport's first
// await, which is what lets it also cover the hole neither watchdog reaches:
// the axios client sets no request timeout, so a `connectUrl` that never
// resolves strands the attempt on both paths. On desktop the poll and the
// callback listener are both downstream of that await; on mobile so is
// `armRedirectWatchdog`, which cannot arm earlier because it has nothing to
// watch until there is a URL to assign. A timeout on the client would be the
// better fix for that half — and would cover every other request besides — so
// this is a backstop there rather than the answer.
//
// Five minutes, not seconds. The popup is the provider's own consent screen and
// a user may legitimately read it, switch accounts, or clear an MFA challenge
// on it, and a tile that gives up under a live popup would be worse than the
// bug — the slowest callback actually observed on this surface landed 106s
// after the click, so this is roughly 3x the real tail. It is a ceiling on
// abandonment, not a latency budget, which is the other reason it is the wrong
// instrument for a hung request — and why each transport restarts it once the
// provider's page is on screen (`armAttemptCeiling`). Without that restart a
// slow request would spend the user's budget before they saw anything, which
// on a request returning near the deadline means closing a consent screen
// seconds after it appeared.
//
// It settles `closed`, never `failed`: we cannot see the provider's page, so we
// do not know whether the user approved, denied, or wandered off, and the
// caller re-reads the server exactly as it does for a dismissed popup — a
// connect that did succeed still ticks. And it closes the popup, so the word it
// emits is true. Leaving the window open instead would strand an OAuth flow
// whose result nothing is listening for any more, and would let the next tap
// retarget this surface's window name onto a different provider mid-consent.
const ATTEMPT_CEILING_MS = 5 * 60 * 1000

interface UseSuggestionConnectOptions {
  // Which surface these tiles are on, for the failure event. Required rather
  // than defaulted so a new mount cannot emit surface-less failures that
  // silently pool with a surface whose flow is nothing like it — the same
  // reason `SuggestedIntegrationsCard` takes it as a prop.
  surface: IntegrationSuggestionSurface
  // Called once per attempt, after the popup resolves or the user closes it.
  // The hook deliberately does not decide what "connected" means: a connected
  // app drops out of the suggestions list server-side, so the caller re-reads
  // that list and reconciles. Keeping the check there means one definition of
  // connected — the server's — instead of the hook trusting a postMessage.
  //
  // The verdict comes back rather than staying in the caller because the
  // counting stays here, for the reason the failure event already gives: a
  // third caller gets it for free, and no surface can quietly stop emitting
  // half the funnel. Most callers can answer with `verdictFromShortlist` above;
  // one with a real per-app connection list should answer from that instead.
  //
  // The return type is total on purpose — a caller that cannot answer must say
  // `unknown`, which is a different row from `not_connected`.
  onSettled: (
    suggestion: ConnectableSuggestion,
    outcome: SettledOutcome,
  ) => SuggestionConnectVerdict | Promise<SuggestionConnectVerdict>
}

interface UseSuggestionConnect {
  connectingKey: string | null
  error: string | null
  clearError: () => void
  connect: (suggestion: ConnectableSuggestion) => Promise<void>
}

/**
 * Opens the connect flow for one inbox-scan suggestion, whatever backs it.
 *
 * Everything runs in a popup — including promoted integrations, which the
 * integrations page connects with a full-page `window.location` assignment.
 * That is fine on a settings page and wrong here: every connect callback
 * returns to `/integrations` (`_integration_app_return_url` hardcodes it), and
 * a full-page assignment tears down the wizard to get there.
 *
 * It is not, however, unrecoverable: `/integrations` sits behind `SetupGuard`,
 * which bounces anyone with `setup_completed_at == null` to `/onboarding`, and
 * `deriveOnboardingStep` resumes on this step — `completeSetup()`, not a
 * seen-marker, is what ends onboarding. What the round trip costs is the local
 * state: `connectedKeys` resets, so an app connected on the way out comes back
 * with no tick (it has simply dropped out of the shortlist), and the grid
 * re-polls from scratch.
 *
 * Mobile takes that cost by necessity — popups are unreliable there, so the
 * flow falls back to a full-page redirect exactly as the rest of the app does
 * (GET-8963 / GET-8975). The step's copy promises the remainder is waiting in
 * Settings, which stays true either way.
 *
 * That redirect is watchdogged (GET-11630). `window.location.assign` can be
 * accepted and then silently not performed — it demonstrably was for a real
 * iOS Safari user — and the mobile branch has no popup to poll and nothing to
 * settle it, so the tile spins forever with no banner and no retry. The
 * watchdog converts that into an ordinary failure, and the failure event makes
 * it countable: before it, "the connect went nowhere" and "the user changed
 * their mind" were the same absence of data.
 *
 * The rule the redirect path follows is that a mobile attempt is settled by
 * whatever happens first — the page dies (nothing to settle), the page comes
 * back (`closed`, ask the server), or neither within the watchdog's window
 * (`failed`). There is no fourth branch in which it stays open, because every
 * one of those was a spinning tile.
 *
 * The popup path now follows the same rule (GET-13089). It was written to the
 * first half of it only — the callback lands, or the user closes the window —
 * and kept the fourth branch for the case where neither happens, which is
 * exactly the case a user cannot recover from: `ATTEMPT_CEILING_MS` is that
 * branch's ceiling. Its absence cost more than one spinning tile, because an
 * attempt that can never settle also never releases the in-flight guard in
 * `connect`, and that guard used to discard every subsequent tap in silence.
 *
 * One ending sits outside that rule, because nothing is left to apply it: a
 * document discarded mid-attempt counts `left_page` on the way out
 * (GET-13139). React does not run cleanup on unload, so before that a closed
 * tab left the attempt with no terminal event at all — and the delivery, not
 * the listener, was the hard half: see `trackEvent`'s `onUnload`.
 *
 * KNOWN GAP: the promoted flow's callback page posts no message and does not
 * close itself, so those four apps resolve only when the user closes the popup
 * (the poll below). Giving that route the `return_mode` treatment the other
 * three already have is the fix — GET-11141.
 */
export function useSuggestionConnect({
  surface,
  onSettled,
}: UseSuggestionConnectOptions): UseSuggestionConnect {
  const [connectingKey, setConnectingKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const popupRef = useRef<Window | null>(null)
  const pollRef = useRef<number | null>(null)
  // The mobile redirect's watchdog, and the teardown for the listeners that
  // cancel it. Both live in refs so `finish` and unmount can cancel a redirect
  // armed by a `connect` several renders ago.
  const redirectTimerRef = useRef<number | null>(null)
  const cancelRedirectListenersRef = useRef<(() => void) | null>(null)
  // The popup-closed poll's grace timer. Held so unmount can cancel it —
  // `stopPolling` clears the interval but this is scheduled *by* the interval
  // and outlives it, so without a handle the cleanup is incomplete and
  // `finish('closed')` still runs against an unmounted component.
  const settleTimerRef = useRef<number | null>(null)
  // The whole attempt's deadline — both transports, and the connect-URL await
  // that precedes either of them. Separate from `settleTimerRef` because they
  // mean opposite things: that one is a grace period after the popup is known
  // to be gone, this one is the deadline for an attempt that will not admit it
  // is over.
  const attemptCeilingRef = useRef<number | null>(null)
  const activeRef = useRef<ConnectableSuggestion | null>(null)
  // What the deferred callbacks below compare against, because the suggestion
  // object cannot: `ToolsStep` renders from a stable array, so re-tapping the
  // tile you just closed hands `connect` the *same* object, and a timer
  // comparing objects would find its own attempt still "active" and settle the
  // new one instead.
  //
  // Two refs rather than a single counter. `nextAttemptIdRef` only counts up,
  // so an id is never reused; `attemptIdRef` names the attempt that is
  // currently open and is reset to 0 by every settle. That second half is what
  // lets a stale callback tell three states apart — still mine, a newer
  // attempt's, and *nobody's* — and the third is exactly what the ceiling
  // produces. A bare counter reports it as "still mine", which on the redirect
  // path means navigating the page to an authorize URL minutes after the
  // attempt behind it was abandoned.
  const nextAttemptIdRef = useRef(0)
  const attemptIdRef = useRef(0)
  // Which transport the open attempt is using. `popupRef` cannot answer this:
  // the redirect path never opens a window, and it has an await between
  // claiming the attempt and arming its watchdog, so "the popup is gone" and "a
  // redirect is in flight" are indistinguishable from the guard in `connect`
  // at precisely the moment the guard has to tell them apart.
  const pendingTransportRef = useRef<'popup' | 'redirect' | null>(null)
  // `onSettled` is typically an inline arrow, so depending on it directly would
  // tear down and re-add the message listener on every render — long enough,
  // occasionally, to miss the callback that arrives in between.
  const settledRef = useRef(onSettled)
  settledRef.current = onSettled
  // Read by the unmount cleanup, which must not list `surface` as a dependency:
  // a surface that ever changed under a mounted component would tear the effect
  // down and report a `left_page` abandonment for an attempt that is still very
  // much in flight.
  const surfaceRef = useRef(surface)
  surfaceRef.current = surface

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current)
      pollRef.current = null
    }

  }, [])

  // Stands the timer down without touching the listeners — what a document
  // going away wants, since it may still come back holding an open attempt.
  const clearRedirectTimer = useCallback(() => {
    if (redirectTimerRef.current !== null) {
      window.clearTimeout(redirectTimerRef.current)
      redirectTimerRef.current = null
    }
  }, [])

  const clearAttemptCeiling = useCallback(() => {
    if (attemptCeilingRef.current !== null) {
      window.clearTimeout(attemptCeilingRef.current)
      attemptCeilingRef.current = null
    }
  }, [])

  // The whole watchdog, listeners included. Unlike the popup-close grace timer
  // below, this is cleared by `finish` as well as by unmount: exactly one thing
  // can settle a redirect, so any settle means it has nothing left to watch.
  const clearRedirectWatchdog = useCallback(() => {
    clearRedirectTimer()
    cancelRedirectListenersRef.current?.()
    cancelRedirectListenersRef.current = null
  }, [clearRedirectTimer])

  const finish = useCallback(
    (...[outcome, failure]: FinishArgs) => {
      const suggestion = activeRef.current
      // Guard against a second completion for one attempt: the callback
      // message and the popup-closed poll both fire in the ordinary success
      // case, and without this the caller re-reads the list twice and the
      // error banner from a failure can be cleared by the close that follows.
      if (suggestion === null) return
      activeRef.current = null
      attemptIdRef.current = 0
      pendingTransportRef.current = null
      stopPolling()
      clearRedirectWatchdog()
      clearAttemptCeiling()
      popupRef.current = null
      setConnectingKey(null)
      if (outcome === 'failed') {
        setError(failure.message)
        // Fired here rather than from the two callers so the reason can never
        // drift from the branch that produced it, and so a third caller gets
        // the counting for free. The click event moved into `connect` for the
        // same argument inverted: the callers know a tap happened, but only
        // `connect` knows whether it was accepted, and a tap the guard refuses
        // must not open an attempt no terminal event can ever close (GET-13089).
        trackEvent(OnboardingEvents.INTEGRATION_SUGGESTION_CONNECT_FAILED, {
          catalog_key: suggestion.catalog_key,
          surface,
          reason: failure.reason,
        })
        return
      }
      // Both remaining outcomes end the state the guard's banner describes, so
      // the banner goes with them. Without this, a user who is told to finish
      // in the other window, and does, is left reading that instruction beside
      // a ticked tile until their next tap — both callers render `error` in a
      // red box. It cannot swallow a real failure banner: the double-settle
      // early return above means one attempt reaches this once, and a failure
      // from a *previous* attempt was already cleared by the `setError(null)`
      // in `connect` that started this one.
      setError(null)
      // The other two outcomes are both "something may have connected", and
      // neither can be counted from here alone: a `connected` postMessage is
      // the provider's claim rather than ours, and a `closed` popup is the
      // *only* thing the promoted flow ever produces on success. So the
      // terminal event waits on the caller's re-read.
      //
      // Awaited in a floating promise rather than by making `finish` async: the
      // double-settle guard above is what makes one attempt emit one event, and
      // it only holds because `activeRef` is cleared synchronously before any
      // of this. The tile has already stopped spinning; only the counting is
      // outstanding.
      void (async () => {
        // A caller that throws is the last way "exactly one terminal event per
        // attempt" could still fail silently: `activeRef` is already cleared,
        // so nothing retries, and an unhandled rejection is the only trace.
        // Both callers today catch their own reads, which is precisely why this
        // would go unnoticed until a third one does not.
        //
        // `unknown` rather than a swallowed error, because that is what it is:
        // we asked and got no usable answer. It lands as `unverified`, the
        // bucket that is deliberately kept out of intent reads.
        let verdict: SuggestionConnectVerdict
        try {
          verdict = await settledRef.current(suggestion, outcome)
        } catch {
          verdict = 'unknown'
        }
        if (outcome === 'connected' || verdict === 'connected') {
          trackEvent(OnboardingEvents.INTEGRATION_SUGGESTION_CONNECTED, {
            catalog_key: suggestion.catalog_key,
            surface,
            settled_by: outcome === 'connected' ? 'callback' : 'popup_closed',
            server_verdict: verdict,
          })
          return
        }
        trackEvent(OnboardingEvents.INTEGRATION_SUGGESTION_CONNECT_ABANDONED, {
          catalog_key: suggestion.catalog_key,
          surface,
          reason: verdict === 'unknown' ? 'unverified' : 'closed_without_connection',
        })
      })()
    },
    [stopPolling, clearRedirectWatchdog, clearAttemptCeiling, surface],
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      if (
        EXPECTED_CALLBACK_ORIGIN !== null
        && event.origin !== EXPECTED_CALLBACK_ORIGIN
      ) return
      const active = activeRef.current
      if (active === null) return
      const data: unknown = event.data
      if (typeof data !== 'object' || data === null) return
      const payload = data as { type?: unknown; success?: unknown; message?: unknown }
      if (payload.type !== expectedMessageType(active)) return
      if (payload.success) {
        finish('connected')
        return
      }
      const detail = typeof payload.message === 'string' ? payload.message : undefined
      finish('failed', {
        message: detail?.replace(/^error:/, '') || 'Connection did not complete.',
        reason: 'callback_failed',
      })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [finish])

  // Counts an attempt the user walked away from, and claims it so nothing
  // counts it twice. Two different things can be what "walked away" means —
  // this component unmounting, and the document being discarded — and they
  // differ only in how the event has to be delivered. Shared rather than
  // written out twice because a `left_page` with two authors is a `left_page`
  // that grows two shapes the first time somebody adds a property to one.
  //
  // Counted rather than settled in both cases: `finish` would re-read the
  // server and set state for a component (or a document) that is on its way
  // out, and the answer would be meaningless anyway, since the connect the user
  // started may complete seconds after this fires.
  //
  // `onUnload` is named at the call site rather than positional: the two
  // callers otherwise read as `countAbandoned(true)` / `countAbandoned(false)`,
  // which say nothing without this comment in view. It is passed explicitly by
  // both — including the unmount path's `false` — so the transport is a stated
  // property of each caller instead of an omission. Beaconing both would delete
  // the flag, but a beacon is size-capped, unretryable and its response
  // unreadable, and an unmount is an ordinary SPA transition with plenty of
  // ticks left to flush on.
  const countAbandoned = useCallback(({ onUnload }: { onUnload: boolean }) => {
    const pending = activeRef.current
    if (pending === null) return
    activeRef.current = null
    attemptIdRef.current = 0
    pendingTransportRef.current = null
    trackEvent(
      OnboardingEvents.INTEGRATION_SUGGESTION_CONNECT_ABANDONED,
      {
        catalog_key: pending.catalog_key,
        surface: surfaceRef.current,
        reason: 'left_page',
      },
      { onUnload },
    )
  }, [])

  useEffect(() => {
    // A `pagehide` this document will not come back from is the other way an
    // open attempt ends without settling, and the cleanup below cannot reach
    // it: React does not run cleanup on unload, so a closed or hard-reloaded
    // tab leaves the attempt with no terminal event anywhere. That is why
    // `left_page` had fired once in 21 days against 34 stranded desktop
    // attempts, and it is the whole of GET-13139.
    //
    // Two conditions guard it, and each excludes a case that would otherwise be
    // counted wrongly:
    //
    //   `pendingTransportRef.current === 'popup'` — a popup attempt, not a
    //   redirect. On mobile a `pagehide` is the flow *working*: it is the
    //   `window.location.assign` this hook just asked for, and counting it
    //   would file every successful mobile connect as a loss. Read through
    //   that ref rather than `popupRef`, for the reason its own comment gives:
    //   it is set when the attempt is claimed, so it is already right during
    //   the await before either transport has done anything.
    //
    //   `!persisted` — the document is being discarded rather than frozen. A
    //   bfcached page can come back still holding this attempt, and the
    //   popup-closed poll settles it normally when it does. Nothing is stood
    //   down for a freeze, deliberately: a partial teardown here would leave a
    //   returning document in a worse state than an untouched one.
    const onPageHide = (event: Event): void => {
      if ((event as PageTransitionEvent).persisted) return
      if (pendingTransportRef.current !== 'popup') return
      countAbandoned({ onUnload: true })
      // Release the UI, but do not tear the flow down. `persisted: false` is a
      // promise this document will not be restored, and on the overwhelmingly
      // likely reading of it none of this runs anywhere observable. On the
      // reading where the page survives anyway, claiming `activeRef` without
      // these leaves the tile spinning against an attempt nothing can settle
      // any more — the poll's `finish('closed')` no-ops through the guard —
      // and leaves a 500ms interval running until unmount.
      //
      // The ceiling goes too, so the cleanup below keeps telling the truth
      // when it says nothing clears `activeRef` without also standing the
      // watchdog down. Left armed it would only fire into the same no-op, but
      // an invariant that holds by luck is one the next edit breaks.
      //
      // Deliberately NOT the unmount cleanup's full teardown: that closes the
      // popup, and on a surviving document that would kill an OAuth flow the
      // user may be halfway through.
      stopPolling()
      clearAttemptCeiling()
      setConnectingKey(null)
    }
    window.addEventListener('pagehide', onPageHide)

    return () => {
      window.removeEventListener('pagehide', onPageHide)
      // Going away with an attempt still open is a third terminal state, and it
      // is the one the onboarding step produces most: the popup is behind the
      // wizard, so hitting Continue unmounts this and closes the popup below
      // with nothing ever settling it.
      countAbandoned({ onUnload: false })
      stopPolling()
      clearRedirectWatchdog()
      // Unlike the grace timer below, this one IS cleared by `finish` as well.
      // Clearing it here too is what lets the arming site skip an "is this still
      // my attempt?" check: between them, the two are the only ways `activeRef`
      // is ever cleared, so the watchdog cannot outlive the attempt it watches.
      clearAttemptCeiling()
      // Deliberately cleared here and NOT in `stopPolling`: that runs from
      // `finish` too, so clearing it there would cancel the queued `closed`
      // settle on every success and make the double-settle guard unreachable
      // rather than redundant. Unmount is the only place this timer should be
      // abandoned.
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current)
        settleTimerRef.current = null
      }
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close()
    }
    // Every dependency here must be referentially stable, and it is not a style
    // point: this effect's cleanup EMITS an abandonment, so a dep that ever
    // changed under a mounted component would report `left_page` for an attempt
    // still in flight. That is the same hazard `surface` is read through
    // `surfaceRef` to avoid — all four of these are `useCallback([])`.
  }, [stopPolling, clearRedirectWatchdog, clearAttemptCeiling, countAbandoned])

  // Arms the post-assign watchdog, plus the events that stand it down when the
  // document turns out to be going somewhere after all.
  //
  // Leaving (`pagehide`, or `visibilitychange` to hidden — the first fires on
  // iOS Safari for both a real unload and a bfcache freeze, the second covers
  // browsers that background the tab before unloading it) cancels the timer, so
  // a slow-but-real navigation never flashes a failure at a user already on
  // their way to the provider.
  //
  // Coming back (`pageshow`, or `visibilitychange` to visible) settles the
  // attempt as `closed`, and that half is not optional: a hidden document is
  // not necessarily a dead one. A bfcache restore — the user reaching the
  // authorize page and tapping Back — brings this exact React state back with
  // `activeRef` still set, and if the listeners had been torn down on the way
  // out there would be no timer, no popup to poll, and no way to retry (the
  // in-flight guard swallows the next tap, and `popupRef.current?.focus()` is a
  // no-op on mobile). The tile would spin forever, which is the failure this
  // whole change exists to remove — just reached by a different road.
  //
  // `closed` rather than `failed` because it is the truthful answer and the
  // hook already has a word for it: we cannot tell from here whether the user
  // approved, denied, or backed out while we were gone, so the caller re-reads
  // the server, exactly as it does when a popup is dismissed. That also means
  // the user who *did* approve and then navigated back gets a ticked tile
  // rather than an error about a connection that worked.
  // Starts (or restarts) the attempt's deadline.
  //
  // Called twice per attempt, and the second time is the point: armed once at
  // the tap, the connect-URL request would spend the abandonment budget before
  // the user ever saw a consent screen — a request coming back at 4m50s would
  // leave ten seconds of it, and the popup would be closed under someone
  // actively reading. Restarting it when the transport really begins keeps
  // ATTEMPT_CEILING_MS what its comment says it is, a ceiling on abandonment
  // rather than a latency budget, and the total stays bounded either way.
  const armAttemptCeiling = useCallback(() => {
    clearAttemptCeiling()
    attemptCeilingRef.current = window.setTimeout(() => {
      attemptCeilingRef.current = null
      const stranded = popupRef.current
      if (stranded !== null && !stranded.closed) stranded.close()
      finish('closed')
    }, ATTEMPT_CEILING_MS)
  }, [clearAttemptCeiling, finish])

  const armRedirectWatchdog = useCallback(
    (suggestion: IntegrationSuggestionItem) => {
      const onPageLifecycleChange = (event: Event): void => {
        const leaving = event.type === 'pagehide'
          || (event.type === 'visibilitychange' && document.visibilityState === 'hidden')
        if (leaving) {
          // The timer only. The listeners stay, because this document may yet
          // come back and still be holding an unsettled attempt.
          clearRedirectTimer()
          return
        }
        // A `pageshow` without `persisted` is this document's own first load,
        // not a return from anywhere — and it can land *after* the tap that
        // armed this, because `load` waits on subresources and these tiles pull
        // brand logos off other origins. Treating it as a return would cancel
        // the watchdog and report `closed` for what is actually a swallowed
        // assign, losing the one signal this change exists to produce. The
        // visibilitychange half still covers restores on browsers where
        // `persisted` is unreliable.
        if (
          event.type === 'pageshow'
          && !(event as PageTransitionEvent).persisted
        ) return
        finish('closed')
      }
      window.addEventListener('pagehide', onPageLifecycleChange)
      window.addEventListener('pageshow', onPageLifecycleChange)
      document.addEventListener('visibilitychange', onPageLifecycleChange)
      cancelRedirectListenersRef.current = () => {
        window.removeEventListener('pagehide', onPageLifecycleChange)
        window.removeEventListener('pageshow', onPageLifecycleChange)
        document.removeEventListener('visibilitychange', onPageLifecycleChange)
      }
      redirectTimerRef.current = window.setTimeout(() => {
        redirectTimerRef.current = null
        finish('failed', {
          message: `The ${suggestion.display_name} connection didn't open. Try again, or connect it later in Settings.`,
          reason: 'navigation_no_op',
        })
      }, REDIRECT_WATCHDOG_MS)
    },
    [finish, clearRedirectTimer],
  )

  const connect = useCallback(
    async (suggestion: ConnectableSuggestion): Promise<void> => {
      const pending = activeRef.current
      if (pending !== null) {
        if (pendingTransportRef.current === 'redirect') {
          // A redirect whose watchdog was armed and then *cancelled* — which
          // only `pagehide`/`visibilitychange` do, meaning this document left
          // and has come back. `pageshow` normally settles that, but a restore
          // where neither event fires leaves the attempt open with no timer
          // behind it, and every tap after it vanishing. The tap itself is the
          // missing evidence: the document is alive and being used, so settle
          // and honour it, exactly as the popup-gone branch below does.
          const armedButStoodDown = cancelRedirectListenersRef.current !== null
            && redirectTimerRef.current === null
          if (!armedButStoodDown) {
            // Either the assign is genuinely in flight (bounded to three
            // seconds by `REDIRECT_WATCHDOG_MS`) or we are still awaiting the
            // connect URL that precedes it. Dropped, and deliberately without a
            // banner: this document is most likely on its way to the provider,
            // and flashing an error onto a page about to vanish is the failure
            // mode `REDIRECT_WATCHDOG_MS` exists to avoid.
            return
          }
          finish('closed')
        } else if (popupRef.current !== null && !popupRef.current.closed) {
          // The case this guard was written for, and the only one where
          // refusing is right: the popup is alive and usually behind the main
          // window. Starting a second connect here would be worse than
          // refusing — `window.open` reuses this surface's window name, so it
          // would navigate a live consent screen away to another provider.
          //
          // What was wrong was doing it in silence. `focus()` raises the window
          // on some platforms and merely flashes a taskbar entry on others, and
          // either way the tile the user actually tapped never responds, so the
          // grid reads as dead — which is what the GET-13089 user reported,
          // going on to tap a third tile that also did nothing.
          popupRef.current.focus()
          setError(
            `Finish connecting ${pending.display_name} in the window that `
            + 'opened, or close it to choose something else.',
          )
          return
        } else {
          // The popup is gone, or was never really there. Nothing is left that
          // could settle this attempt, so settling it here is the difference
          // between honouring the tap and discarding it: `closed` is the honest
          // outcome, the caller re-reads the server, and a connect that did
          // complete is still counted. Then fall through and start the new one.
          finish('closed')
        }
      }
      // Claimed before anything can await, so every deferred settle below can
      // ask "is this still my attempt?" without comparing suggestion objects.
      const attemptId = ++nextAttemptIdRef.current
      attemptIdRef.current = attemptId
      activeRef.current = suggestion
      const mobile = isMobileBrowser()
      pendingTransportRef.current = mobile ? 'redirect' : 'popup'
      setConnectingKey(suggestion.catalog_key)
      setError(null)
      // Emitted once the tap is accepted, so that every click event has an
      // attempt behind it and the funnel's own invariant — exactly one terminal
      // event per click — is true rather than nearly true.
      trackEvent(OnboardingEvents.INTEGRATION_SUGGESTION_CONNECT_CLICKED, {
        catalog_key: suggestion.catalog_key,
        surface,
      })
      // Armed here rather than inside either transport: both reach their first
      // `await` before they have anything to watch, so a branch that armed its
      // own would always leave that await uncovered. Every exit below settles
      // through `finish`, which clears it. Each transport restarts it once the
      // provider's page is actually on screen — see `armAttemptCeiling`.
      armAttemptCeiling()

      if (mobile) {
        const failToStart = (err: unknown): void => {
          finish('failed', {
            message: getUserFacingApiErrorMessage(
              err,
              `Could not start the ${suggestion.display_name} connection.`,
            ),
            reason: 'url_error',
          })
        }
        let redirectUrl: string
        try {
          redirectUrl = await integrationSuggestionsService.connectUrl(
            suggestion,
            'redirect',
          )
        } catch (err) {
          // The ceiling may have given up while this was in flight, in which
          // case settling now lands a failure — and a banner naming an app the
          // user has moved on from — on whatever attempt is current instead.
          if (attemptIdRef.current !== attemptId) return
          failToStart(err)
          return
        }
        // The same check, and it matters more here than on the popup path:
        // what follows is not a timer to be cancelled but a navigation. A
        // resolve arriving after the ceiling gave up would send the whole page
        // to an authorize URL for an attempt abandoned minutes ago.
        if (attemptIdRef.current !== attemptId) return
        // The request is done; the budget from here is the user's, not ours.
        armAttemptCeiling()
        try {
          // Armed before the assign, not after: an assign that throws must
          // still leave a watchdog behind for `finish` to cancel, and the
          // ordering costs nothing since navigation is never synchronous.
          // It cannot move any earlier than this — there is nothing to watch
          // until there is a URL to assign — which is why the await above is
          // covered by `ATTEMPT_CEILING_MS` instead.
          armRedirectWatchdog(suggestion)
          window.location.assign(redirectUrl)
        } catch (err) {
          // Synchronous, immediately after the check above, so this one needs
          // no staleness guard of its own.
          failToStart(err)
        }
        return
      }

      // Opened before the await, not after: a popup opened in a promise
      // continuation has lost the user-gesture context browsers require, so it
      // is blocked. The blank window is filled in once the URL resolves.
      // Named per surface, not per hook. Two of these are mounted at once on
      // the integrations page — the suggestions row and the catalog card — and
      // `window.open` with a name that is already taken RETARGETS that window
      // rather than opening a second one. A shared name therefore navigates a
      // connect that is still in flight away to a different provider, killing
      // it silently. The card used to avoid this only by accident, having its
      // own hand-rolled flow under its own window name.
      //
      // This removes the collision, not the double-settle behind it: both hooks
      // still listen for the same callback message, so if two attempts really
      // are open at once one callback settles both. That predates this and is
      // guarded, per instance, by `activeRef`.
      const popup = window.open(
        '',
        `suggestion-connect-${surface}`,
        'width=600,height=720,toolbar=no,menubar=no',
      )
      if (!popup) {
        finish('failed', {
          message: 'Popup was blocked. Please allow popups and try again.',
          reason: 'popup_blocked',
        })
        return
      }
      popupRef.current = popup
      popup.document.title = `Connecting ${suggestion.display_name}`
      popup.document.body.style.cssText =
        'font-family:system-ui;display:grid;place-items:center;height:100vh;margin:0;color:#4b5563'
      popup.document.body.textContent = `Connecting ${suggestion.display_name}…`

      let url: string
      try {
        url = await integrationSuggestionsService.connectUrl(suggestion)
      } catch (err) {
        // The watchdog may have given up while this was in flight, in which
        // case the attempt is over and possibly replaced — settling it again
        // would close whatever the user started next, under a banner naming an
        // app they have moved on from.
        if (attemptIdRef.current !== attemptId) return
        popup.close()
        finish('failed', {
          message: getUserFacingApiErrorMessage(
            err,
            `Could not start the ${suggestion.display_name} connection.`,
          ),
          reason: 'url_error',
        })
        return
      }
      if (attemptIdRef.current !== attemptId) return
      // Same restart as the redirect path: the popup is about to leave our
      // placeholder for the provider's consent screen, which is what the
      // ceiling is actually there to bound.
      armAttemptCeiling()
      popup.location.href = url

      pollRef.current = window.setInterval(() => {
        if (!popup.closed) return
        stopPolling()
        // The callback message normally wins. If the user simply closes the
        // window we cannot tell success from abandonment, so report `closed`
        // and let the caller ask the server — some flows (the promoted
        // credential page especially) complete without posting anything back.
        settleTimerRef.current = window.setTimeout(() => {
          settleTimerRef.current = null
          // Bound to the attempt that armed it. `finish` deliberately leaves
          // this timer running (see the unmount cleanup), which was harmless
          // only while one unsettled attempt blocked every other tap. Now that
          // the guard above can settle a dead attempt and start a new one
          // inside this 400ms window, an unbound timer would settle the *new*
          // attempt instead — closing a popup-connect the user had only just
          // opened. By id and not by suggestion: re-tapping the tile you just
          // closed is the likeliest way to land in that window, and it arrives
          // holding the very same object.
          if (attemptIdRef.current !== attemptId) return
          finish('closed')
        }, 400)
      }, 500)
    },
    [finish, stopPolling, armRedirectWatchdog, armAttemptCeiling, surface],
  )

  return {
    connectingKey,
    error,
    clearError: useCallback(() => setError(null), []),
    connect,
  }
}
